(function () {
  'use strict';

  var MARKER_CLUSTERER_SRC = 'https://unpkg.com/@googlemaps/markerclusterer@2.5.3/dist/index.min.js';

  function haversineMiles(lat1, lng1, lat2, lng2) {
    var R = 3958.8; // Earth radius in miles
    var toRad = function (d) { return (d * Math.PI) / 180; };
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function pinIcon(color) {
    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">' +
      '<path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 25 15 25s15-14.5 15-25C30 6.7 23.3 0 15 0z" fill="' +
      color +
      '"/>' +
      '<circle cx="15" cy="15" r="6" fill="#fff"/>' +
      '</svg>';
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: { width: 30, height: 40 },
      anchor: { x: 15, y: 40 },
    };
  }

  function loadScriptOnce(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-src="' + src + '"]');
      if (existing) {
        if (existing.dataset.loaded === 'true') resolve();
        else existing.addEventListener('load', function () { resolve(); });
        return;
      }
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.dataset.src = src;
      script.addEventListener('load', function () {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', function () { reject(new Error('Failed to load ' + src)); });
      document.head.appendChild(script);
    });
  }

  function loadGoogleMaps(apiKey) {
    if (window.google && window.google.maps) return Promise.resolve();
    return new Promise(function (resolve, reject) {
      if (!apiKey) { reject(new Error('missing_api_key')); return; }
      window.__dealerLocatorGMapsReady = function () { resolve(); };
      var script = document.createElement('script');
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(apiKey) +
        '&callback=__dealerLocatorGMapsReady&loading=async';
      script.async = true;
      script.defer = true;
      script.addEventListener('error', function () { reject(new Error('gmaps_load_error')); });
      document.head.appendChild(script);
    });
  }

  function initInstance(root) {
    var dealersUrl = root.dataset.dealersUrl;
    var apiKey = root.dataset.apiKey;
    var defaultZoom = parseInt(root.dataset.defaultZoom, 10) || 4;
    var pinColor = root.dataset.pinColor || '#1a73e8';
    var premierePinColor = root.dataset.premierePinColor || '#e8871a';

    var listEl = root.querySelector('[data-dealer-list]');
    var mapEl = root.querySelector('[data-dealer-map]');
    var mapFallbackEl = root.querySelector('[data-dealer-map-fallback]');
    var statusEl = root.querySelector('[data-dealer-status]');
    var searchInput = root.querySelector('[data-dealer-search-input]');
    var countryFilter = root.querySelector('[data-dealer-country-filter]');
    var nearMeBtn = root.querySelector('[data-dealer-near-me]');
    var itemTemplate = root.querySelector('[data-dealer-list-item-template]');

    var state = {
      dealers: [],
      userLocation: null,
      map: null,
      infoWindow: null,
      markersById: {},
      clusterer: null,
      activeId: null,
    };

    function setStatus(message, tone) {
      statusEl.textContent = message || '';
      if (tone) statusEl.setAttribute('data-tone', tone);
      else statusEl.removeAttribute('data-tone');
    }

    function matchesQuery(dealer, query) {
      if (!query) return true;
      var haystack = [dealer.name, dealer.city, dealer.state, dealer.zip, dealer.country]
        .join(' ')
        .toLowerCase();
      return haystack.indexOf(query.toLowerCase()) !== -1;
    }

    function getFiltered() {
      var query = searchInput.value.trim();
      var country = countryFilter.value;
      var filtered = state.dealers.filter(function (d) {
        if (country && d.country !== country) return false;
        return matchesQuery(d, query);
      });

      if (state.userLocation) {
        filtered.forEach(function (d) {
          d._distance =
            d.lat != null
              ? haversineMiles(state.userLocation.lat, state.userLocation.lng, d.lat, d.lng)
              : null;
        });
        filtered.sort(function (a, b) {
          if (a._distance == null) return 1;
          if (b._distance == null) return -1;
          return a._distance - b._distance;
        });
      } else {
        filtered.sort(function (a, b) { return a.name.localeCompare(b.name); });
      }

      return filtered;
    }

    function renderList() {
      var filtered = getFiltered();
      listEl.innerHTML = '';

      if (!filtered.length) {
        var empty = document.createElement('p');
        empty.className = 'dealer-locator__list-empty';
        empty.textContent = 'No dealers match your search.';
        listEl.appendChild(empty);
        return;
      }

      filtered.forEach(function (dealer) {
        var node = itemTemplate.content.firstElementChild.cloneNode(true);
        node.dataset.dealerId = dealer.id;
        node.querySelector('[data-dealer-item-name]').textContent = dealer.name;
        node.querySelector('[data-dealer-item-address]').textContent = formatAddress(dealer);

        var badge = node.querySelector('[data-dealer-item-badge]');
        if (dealer.premiere) badge.hidden = false;

        var distanceEl = node.querySelector('[data-dealer-item-distance]');
        if (dealer._distance != null) {
          distanceEl.hidden = false;
          distanceEl.textContent = Math.round(dealer._distance) + ' mi away';
        } else if (dealer.lat == null) {
          distanceEl.hidden = false;
          distanceEl.textContent = 'Map location unavailable';
        }

        if (dealer.id === state.activeId) node.setAttribute('aria-current', 'true');

        node.addEventListener('click', function () { selectDealer(dealer.id, true); });
        listEl.appendChild(node);
      });
    }

    function formatAddress(dealer) {
      var line = [dealer.address, dealer.city].filter(Boolean).join(', ');
      var regionZip = [dealer.state, dealer.zip].filter(Boolean).join(' ');
      var tail = [regionZip, dealer.country !== 'United States' ? dealer.country : ''].filter(Boolean).join(', ');
      return [line, tail].filter(Boolean).join(', ');
    }

    function populateCountryFilter() {
      var countries = Array.from(new Set(state.dealers.map(function (d) { return d.country; }))).sort(function (a, b) {
        if (a === 'United States') return -1;
        if (b === 'United States') return 1;
        return a.localeCompare(b);
      });
      countries.forEach(function (country) {
        var option = document.createElement('option');
        option.value = country;
        option.textContent = country;
        countryFilter.appendChild(option);
      });
    }

    function selectDealer(id, panMap) {
      state.activeId = id;
      var dealer = state.dealers.find(function (d) { return d.id === id; });
      if (!dealer) return;

      Array.prototype.forEach.call(listEl.children, function (node) {
        if (node.dataset.dealerId === String(id)) node.setAttribute('aria-current', 'true');
        else node.removeAttribute('aria-current');
      });

      if (!state.map || dealer.lat == null) return;

      var marker = state.markersById[id];
      if (panMap) {
        state.map.panTo({ lat: dealer.lat, lng: dealer.lng });
        state.map.setZoom(Math.max(state.map.getZoom(), 11));
      }
      openInfoWindow(dealer, marker);
    }

    function openInfoWindow(dealer, marker) {
      if (!state.infoWindow || !marker) return;
      var html =
        '<div class="dealer-locator__info-window">' +
        '<strong>' + escapeHtml(dealer.name) + '</strong>' +
        escapeHtml(formatAddress(dealer)) +
        (dealer.premiere ? '<span class="dealer-locator__info-badge">Premiere Dealer</span>' : '') +
        '</div>';
      state.infoWindow.setContent(html);
      state.infoWindow.open({ map: state.map, anchor: marker });
    }

    function escapeHtml(str) {
      var div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    function renderMarkers() {
      if (!state.map) return;
      var geocoded = state.dealers.filter(function (d) { return d.lat != null; });

      var markers = geocoded.map(function (dealer) {
        var marker = new google.maps.Marker({
          position: { lat: dealer.lat, lng: dealer.lng },
          title: dealer.name,
          icon: pinIcon(dealer.premiere ? premierePinColor : pinColor),
        });
        marker.addListener('click', function () {
          selectDealer(dealer.id, false);
          openInfoWindow(dealer, marker);
        });
        state.markersById[dealer.id] = marker;
        return marker;
      });

      if (window.markerClusterer && window.markerClusterer.MarkerClusterer) {
        state.clusterer = new window.markerClusterer.MarkerClusterer({ map: state.map, markers: markers });
      } else {
        markers.forEach(function (m) { m.setMap(state.map); });
      }

      if (markers.length) {
        var bounds = new google.maps.LatLngBounds();
        markers.forEach(function (m) { bounds.extend(m.getPosition()); });
        state.map.fitBounds(bounds);
      }
    }

    function handleSearchOrFilterChange() {
      renderList();
    }

    function handleNearMe() {
      if (!navigator.geolocation) {
        setStatus('Location services are not available in this browser.', 'error');
        return;
      }
      nearMeBtn.setAttribute('aria-busy', 'true');
      setStatus('Finding your location…');
      navigator.geolocation.getCurrentPosition(
        function (position) {
          state.userLocation = { lat: position.coords.latitude, lng: position.coords.longitude };
          nearMeBtn.removeAttribute('aria-busy');
          setStatus('Showing dealers nearest to you.');
          if (state.map) {
            state.map.panTo(state.userLocation);
            state.map.setZoom(9);
          }
          renderList();
        },
        function () {
          nearMeBtn.removeAttribute('aria-busy');
          setStatus('Could not determine your location. Check your browser permissions.', 'error');
        },
        { timeout: 10000 }
      );
    }

    function bootMap() {
      return loadGoogleMaps(apiKey)
        .then(function () {
          return loadScriptOnce(MARKER_CLUSTERER_SRC).catch(function () {
            // Clustering is an enhancement; proceed without it if it fails to load.
          });
        })
        .then(function () {
          state.map = new google.maps.Map(mapEl, {
            zoom: defaultZoom,
            center: { lat: 39.8283, lng: -98.5795 },
            mapTypeControl: false,
            streetViewControl: false,
          });
          state.infoWindow = new google.maps.InfoWindow();
          renderMarkers();
        })
        .catch(function (err) {
          mapFallbackEl.hidden = false;
          if (err && err.message === 'missing_api_key') {
            mapFallbackEl.textContent = 'Add a Google Maps API key in this section’s settings to show the map.';
          }
        });
    }

    fetch(dealersUrl)
      .then(function (res) { return res.json(); })
      .then(function (dealers) {
        state.dealers = dealers;
        populateCountryFilter();
        renderList();
        return bootMap();
      })
      .catch(function () {
        setStatus('Unable to load dealer data right now.', 'error');
      });

    searchInput.addEventListener('input', handleSearchOrFilterChange);
    countryFilter.addEventListener('change', handleSearchOrFilterChange);
    nearMeBtn.addEventListener('click', handleNearMe);
  }

  function init() {
    var roots = document.querySelectorAll('[data-dealer-locator]');
    roots.forEach(initInstance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
